<template>
    <section class="p-3">
        <FormSection
            :full-height="false"
            title="EVV Details"
            :application-id="visitDetails.Application"
            :show-product-pill="true"
            :show-background="true"
            :use-gray-theme="true"
        >
            <template #content>
                <template v-if="isLoading">
                    <div
                        class="p-5 d-flex flex-column align-items-center justify-content-center text-center text-primary"
                    >
                        <i class="fa fa-spinner fa-spin font-size-5xl me-2 animated slow flash infinite"></i>
                        <strong class="mt-1">
                            <b>Loading...</b>
                        </strong>
                    </div>
                </template>
                <template class="dialog position-relative" v-else>
                    <div class="dialog container ps-5 pt-3 row col-12" v-if="!isData">
                        <h5 class="fw-bold">Error</h5>
                        <p class="font-size-xl mb-1">
                            There was problem while loading the EVV Details. Please try again.
                        </p>
                        <span class="mb-2"> If the problem persists, report it. </span>
                        <button class="btn btn-primary w-20 ps-3" @click="getEvvDetails()">Try Again</button>
                    </div>
                    <div class="container row m-0 p-1" v-else>
                        <div class="dialog container row col-sm-12 col-md-6 mt-2">
                            <div class="col-5 ps-3 pe-0">
                                <div class="mt-2"><strong>Client</strong></div>
                                <hr />
                                <div><strong>MRN</strong></div>
                                <hr />
                                <div><strong>Date of Birth</strong></div>
                                <hr />
                                <div><strong>Start of Care</strong></div>
                                <hr />
                                <div><strong>Task</strong></div>
                                <hr />
                                <div><strong>Payment Source</strong></div>
                                <hr />
                                <div><strong>Assigned To</strong></div>
                                <hr />
                                <div><strong>Visit Date</strong></div>
                                <hr />
                                <div><strong>Scheduled Time In</strong></div>
                                <hr />
                                <div><strong>Scheduled Time Out</strong></div>
                                <hr />
                                <div><strong>EVV Time In</strong></div>
                                <hr />
                                <div><strong>EVV Time Out</strong></div>
                                <hr />
                                <div><strong>Adjusted Time In</strong></div>
                                <hr />
                                <div><strong>Adjusted Time Out</strong></div>
                                <hr />
                            </div>
                            <div class="col-7 ps-0 pe-4">
                                <div class="mt-2 text-end">{{ evvDetails.PatientName }}</div>
                                <hr />
                                <div class="text-end">{{ evvDetails.MRN }}</div>
                                <hr />
                                <div class="text-end">{{ formatDateFromDate(evvDetails.DateOfBirth) }}</div>
                                <hr />
                                <div class="text-end">{{ formatDateFromDate(evvDetails.StartOfCare) }}</div>
                                <hr />
                                <div class="text-end">{{ evvDetails.TaskName }}</div>
                                <hr />
                                <div class="text-end">{{ evvDetails.PayorName }}</div>
                                <hr />
                                <div class="text-end">{{ evvDetails.UserName }}</div>
                                <hr />
                                <div class="text-end">{{ formatDateFromDate(evvDetails.VisitStartTime) }}</div>
                                <hr />
                                <div class="text-end">{{ formatHcEvvDate(evvDetails.ScheduledStartTime) }}</div>
                                <hr />
                                <div class="text-end">{{ formatHcEvvDate(evvDetails.ScheduledEndTime) }}</div>
                                <hr />
                                <div class="text-end">{{ formatHcEvvDate(evvDetails.StartTime) }}</div>
                                <hr />
                                <div class="text-end">{{ formatHcEvvDate(evvDetails.EndTime) }}</div>
                                <hr />
                                <div class="text-end">
                                    {{ formatHcEvvDate(evvDetails.AdjustedTimeIn, evvDetails.EvvStatus > 0) }}
                                </div>
                                <hr />
                                <div class="text-end">
                                    {{ formatHcEvvDate(evvDetails.AdjustedTimeOut, evvDetails.EvvStatus > 0) }}
                                </div>
                                <hr />
                            </div>
                            <div class="row ps-4 pe-3">
                                <div class="row px-0">
                                    <div class="col-3 ps-3"><strong>Client Address</strong></div>
                                    <div class="text-end col-9 pe-0">
                                        {{ evvDetails.PatientAddress.Line1 }} {{ evvDetails.PatientAddress.Line2 }}
                                    </div>
                                </div>
                                <div class="text-end">
                                    {{
                                        evvDetails.PatientAddress.Locality +
                                        " " +
                                        evvDetails.PatientAddress.AdministrativeArea +
                                        " " +
                                        evvDetails.PatientAddress.PostalCode +
                                        "-" +
                                        evvDetails.PatientAddress.PostalCodeAdditional
                                    }}
                                </div>
                                <hr />
                            </div>
                            <div class="row ps-4 pe-3">
                                <div class="row px-0">
                                    <div class="col-4 ps-3"><strong>Start EVV Address</strong></div>
                                    <div class="text-end col-8 pe-0" v-if="evvDetails.StartAddress?.PostalCode">
                                        {{ evvDetails.StartAddress.Line1 }} {{ evvDetails.StartAddress.Line2 }}
                                    </div>
                                    <div class="text-end col-9 pe-0" v-else>
                                        {{
                                            evvDetails.IsCommunitySelectedAtStart
                                                ? "Community Services"
                                                : "Not Collected"
                                        }}
                                    </div>
                                </div>
                                <div class="text-end" v-if="evvDetails.StartAddress?.PostalCode">
                                    {{
                                        evvDetails.StartAddress.Locality +
                                        " " +
                                        evvDetails.StartAddress.AdministrativeArea +
                                        " " +
                                        evvDetails.StartAddress.PostalCode +
                                        "-" +
                                        evvDetails.StartAddress.PostalCodeAdditional
                                    }}
                                </div>
                                <hr />
                            </div>
                            <div class="row ps-4 pe-3">
                                <div class="row px-0">
                                    <div class="col-3 ps-3"><strong>End EVV Address</strong></div>
                                    <div class="text-end col-9 pe-0" v-if="evvDetails.EndAddress?.PostalCode">
                                        {{ evvDetails.EndAddress.Line1 }} {{ evvDetails.EndAddress.Line2 }}
                                    </div>
                                    <div class="text-end col-9 pe-0" v-else>
                                        {{
                                            evvDetails.IsCommunitySelectedAtEnd ? "Community Services" : "Not Collected"
                                        }}
                                    </div>
                                </div>
                                <div class="text-end pe-0" v-if="evvDetails.EndAddress?.PostalCode">
                                    {{
                                        evvDetails.EndAddress.Locality +
                                        " " +
                                        evvDetails.EndAddress.AdministrativeArea +
                                        " " +
                                        evvDetails.EndAddress.PostalCode +
                                        "-" +
                                        evvDetails.EndAddress.PostalCodeAdditional
                                    }}
                                </div>
                                <hr />
                            </div>
                        </div>
                        <div class="col-sm-12 col-md-6 mt-2">
                            <div class="form-group" v-if="evvDetails.PatientSignatureType !== 2">
                                <label for="comments"> Comments </label>
                                <textarea
                                    class="form-control"
                                    rows="4"
                                    v-model="evvDetails.Comments"
                                    :disabled="!isEditing"
                                ></textarea>
                            </div>
                            <div
                                class="form-group"
                                v-if="evvDetails.PatientSignatureUrl && evvDetails.PatientSignatureType == 1"
                            >
                                <label for="comments"> Client Signature </label>
                                <img class="img-fluid" :src="evvDetails.PatientSignatureUrl" />
                            </div>
                            <div
                                class="form-group pb-3 pt-2 row"
                                v-if="evvDetails.PatientSignatureUrl && evvDetails.PatientSignatureType == 2"
                            >
                                <label for="comments"> Client Signature (Voice Recording) </label>
                                <audio
                                    class="col-7"
                                    controls
                                    controlslist="nodownload"
                                    :src="evvDetails.PatientSignatureUrl"
                                ></audio>
                                <div class="col-12 pt-2">
                                    <a
                                        class="btn btn-primary w-25"
                                        :href="evvDetails.PatientSignatureUrl"
                                        download
                                        status="Download Audio"
                                        >Download Audio</a
                                    >
                                </div>
                            </div>
                            <div class="form-group pb-3" v-if="evvDetails.CaretakerSignatureUrl">
                                <label for="comments"> Caretaker Signature </label>
                                <img class="img-fluid" :src="evvDetails.CaretakerSignatureUrl" />
                            </div>
                            <div class="form-group pb-3" v-if="evvDetails.ClinicianSignatureUrl">
                                <label for="comments"> Clinician Signature </label>
                                <img class="img-fluid" :src="evvDetails.ClinicianSignatureUrl" />
                            </div>
                        </div>
                        <section class="d-flex flex-column col-12 px-3 py-2">
                            <div class="row">
                                <img class="offset-lg-2 col-7" :src="evvDetails.MapsUrl" />
                                <ul class="list-unstyled col-3 ps-4">
                                    <li><i class="fa fa-map-marker-alt fa-2x text-danger p-2"></i> Client Address</li>
                                    <li>
                                        <i class="fa fa-map-marker-alt fa-2x text-primary p-2"></i> EVV Start Location
                                    </li>
                                    <li>
                                        <i class="fa fa-map-marker-alt fa-2x text-success p-2"></i> EVV End Location
                                    </li>
                                </ul>
                            </div>
                        </section>
                    </div>
                    <div
                        class="d-flex justify-content-center mb-3 position-sticky bg-background-home-care-form-section p-2 z-index-10 bottom-0"
                        v-if="!isLoading"
                    >
                        <button type="button" class="ms-1 btn btn-md btn-outline-secondary" @click="closeDialog()">
                            Cancel
                        </button>
                    </div>
                </template>
            </template>
        </FormSection>
    </section>
</template>

<style scoped>
hr {
    margin: 0.5rem 0rem 0.5rem 0rem !important;
}
</style>

<script src="./EvvDetailsHomeCare.ts"></script>
